import React from 'react';
import { cn } from '../../utils/cn';

export interface CardProps {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  /** Transitions to the DFX5 gradient-tinted shadow (`--shadow-gradient`) on hover. */
  hoverable?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  onClick?: () => void;
  style?: React.CSSProperties;
}

const Card: React.FC<CardProps> = ({
  children,
  className,
  hover = false,
  hoverable = false,
  padding = 'md',
  onClick,
  style,
}) => {
  const base = 'bg-[var(--card)] text-[var(--card-foreground)] rounded-[var(--radius-2xl)] border border-[var(--border)] shadow-[var(--shadow-sm)] transition-shadow duration-300 ease-[var(--ease)]';
  const hoverStyles = hover ? 'hover:shadow-md cursor-pointer' : '';
  const hoverableStyles = hoverable ? 'hover:shadow-[var(--shadow-gradient)]' : '';

  const paddings: Record<string, string> = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  return (
    <div
      className={cn(base, hoverStyles, hoverableStyles, paddings[padding], className)}
      onClick={onClick}
      style={style}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {children}
    </div>
  );
};

export const CardHeader: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('flex flex-col gap-1.5 pb-4', className)}>{children}</div>
);

export const CardTitle: React.FC<{ children: React.ReactNode; className?: string; id?: string }> = ({ children, className, id }) => (
  <h3 id={id} className={cn('text-lg font-semibold tracking-tight text-[var(--foreground)]', className)}>{children}</h3>
);

export const CardDescription: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <p className={cn('text-sm text-[var(--muted-foreground)]', className)}>{children}</p>
);

export const CardContent: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('', className)}>{children}</div>
);

export const CardFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('flex items-center pt-4', className)}>{children}</div>
);

export default Card;
